import type { ButtonHTMLAttributes, ReactNode } from "react";
import { Spinner } from "../Spinner.tsx";

export type ButtonVariant =
  | "primary"
  | "secondary"
  | "outline"
  | "ghost"
  | "danger";

export type ButtonSize = "sm" | "md" | "lg";

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  icon?: ReactNode;
};

const variantClasses: Record<ButtonVariant, string> = {
  primary:
    "bg-purple-600 hover:bg-purple-700 text-white shadow-lg shadow-purple-500/20 active:scale-95",
  secondary: "bg-white/10 hover:bg-white/20 text-white",
  outline:
    "border border-white/10 bg-white/5 hover:bg-white/10 text-gray-300 hover:text-white",
  ghost: "text-gray-400 hover:text-white hover:bg-white/5",
  danger:
    "bg-red-600/20 hover:bg-red-600/30 text-red-300 border border-red-500/30",
};

const sizeClasses: Record<ButtonSize, string> = {
  sm: "px-3 py-1.5 text-xs font-medium rounded-lg gap-1.5",
  md: "px-4 py-2 text-sm font-medium rounded-lg gap-2",
  lg: "px-6 py-3 text-base font-semibold rounded-xl gap-2.5",
};

export const Button = ({
  variant = "primary",
  size = "md",
  loading = false,
  icon,
  children,
  className = "",
  disabled,
  type = "button",
  ...rest
}: ButtonProps) => (
  <button
    type={type}
    disabled={disabled || loading}
    className={`inline-flex items-center justify-center transition-all disabled:opacity-50 disabled:pointer-events-none disabled:cursor-not-allowed ${
      variantClasses[variant]
    } ${sizeClasses[size]} ${className}`}
    {...rest}
  >
    {loading ? <Spinner className="h-4 w-4 shrink-0" /> : icon}
    {children}
  </button>
);
